import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import NavigationHeader from "@/components/NavigationHeader";
import Footer from "@/components/Footer";
import CookieBanner, { getCookieConsent } from "@/components/CookieBanner";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import Home from "@/pages/Home";
import ChiSiamo from "@/pages/ChiSiamo";
import Corsi from "@/pages/Corsi";
import Contatti from "@/pages/Contatti";
import CookiePolicy from "@/pages/CookiePolicy";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Recesso from "@/pages/Recesso";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Admin from "@/pages/Admin";

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PageTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const consent = getCookieConsent();
    if (consent?.analytics && !pathname.startsWith("/admin")) {
      api.post("/analytics/pageview", { path: pathname }).catch(() => {});
    }
  }, [pathname]);
  return null;
};

const SEO_MAP = {
  "/": {
    title: "Corsi di Informatica a Obiettivo a Brescia | Scuola Didattica Sicura",
    description: "Corsi pratici di informatica per principianti e senior: corso base, web design e specializzazioni a Brescia e in tutta Italia. Metodo a obiettivo con Garanzia di Profitto. Tel 030 2470948.",
  },
  "/chi-siamo": {
    title: "Chi Siamo — Metodo e Insegnanti | Scuola Didattica Sicura",
    description: "Un team di professionisti della formazione informatica: insegnanti preparati, metodo pratico a obiettivo e classi a numero chiuso. Scopri la nostra scuola.",
  },
  "/corsi": {
    title: "Corsi di Informatica: Base, Web Design e Specializzazioni | Scuola Didattica Sicura",
    description: "Corso base di informatica, web design e specializzazioni (AutoCAD, Photoshop, 3D, video editing): corsi a obiettivo, non a ore, per tutti i livelli.",
  },
  "/contatti": {
    title: "Contatti — Scuola Didattica Sicura Brescia | Tel 030 2470948",
    description: "Contattaci per informazioni sui corsi di informatica: Via Sostegno 25, 24124 Brescia. Tel 030 2470948, lun-ven 10.00-16.00, info@scuoladidatticasicura.it.",
  },
  "/blog": {
    title: "Blog — Guide e Consigli di Informatica | Scuola Didattica Sicura",
    description: "Guide passo passo, consigli pratici e articoli di informatica pensati per chi parte da zero: dal blog di Scuola Didattica Sicura.",
  },
  "/recesso": {
    title: "Diritto di Recesso e Modulo | Scuola Didattica Sicura",
    description: "Informativa sul diritto di recesso ex D.Lgs. 21/2014 e modulo di recesso tipo scaricabile in PDF.",
  },
  "/cookie-policy": {
    title: "Cookie Policy | Scuola Didattica Sicura",
    description: "Come questo sito utilizza i cookie e come gestire il consenso: cookie tecnici, analitici e di profilazione.",
  },
  "/privacy-policy": {
    title: "Privacy Policy (GDPR) | Scuola Didattica Sicura",
    description: "Informativa privacy ai sensi del Reg. UE 2016/679: come trattiamo i dati che ci affidi tramite il sito.",
  },
};

const RouteSeo = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/blog/") || pathname.startsWith("/admin")) return null;
  const seo = SEO_MAP[pathname] || SEO_MAP["/"];
  return <Seo title={seo.title} description={seo.description} path={pathname} />;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-white text-[#1A2E33]">
        <BrowserRouter>
          <ScrollManager />
          <PageTracker />
          <RouteSeo />
          <NavigationHeader />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/chi-siamo" element={<ChiSiamo />} />
            <Route path="/corsi" element={<Corsi />} />
            <Route path="/contatti" element={<Contatti />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/recesso" element={<Recesso />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
          <Footer />
          <CookieBanner />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
